export type SheetState = 'collapsed' | 'partial' | 'full'

interface ResultsSheetOptions {
  panel: HTMLElement
  sheet: HTMLElement
  content: HTMLElement
  handle: HTMLButtonElement
  restore: HTMLButtonElement
  map: HTMLElement
  availableHeight: () => number
  mapIsUsable: () => boolean
  compact: MediaQueryList
  onChange: () => void
}

export function sheetGeometry(frameHeight: number, headerHeight: number) {
  const full = Math.max(0, frameHeight)
  return { collapsed: 0, partial: Math.min(full, Math.max(full / 3, headerHeight + 96)), full }
}

// Ties prefer the less expanded state. Degenerate frames remain deterministic.
export function nearestSheetState(height: number, geometry: ReturnType<typeof sheetGeometry>): SheetState {
  const states: SheetState[] = ['collapsed', 'partial', 'full']
  return states.reduce((nearest, candidate) =>
    Math.abs(height - geometry[candidate]) < Math.abs(height - geometry[nearest]) ? candidate : nearest)
}

export function stepSheetState(state: SheetState, direction: 'up' | 'down'): SheetState {
  const states: SheetState[] = ['collapsed', 'partial', 'full']
  return states[Math.max(0, Math.min(2, states.indexOf(state) + (direction === 'up' ? 1 : -1)))]
}

export function createResultsSheet(options: ResultsSheetOptions) {
  const { panel, sheet, content, handle, restore, map, compact, onChange } = options
  const controls = handle.parentElement!
  let state: SheetState = 'partial'
  let enabled = false
  let geometry = sheetGeometry(0, 0)
  let lastUpdate = ''
  let suppressClick = false
  let drag: { id: number; startY: number; startHeight: number; height: number; moved: boolean } | undefined

  function focus(element: HTMLElement) {
    // A navigation/hidden-results refresh must never steal focus.
    if (!panel.closest('[hidden], [inert]') && panel.getClientRects().length) {
      element.focus({ preventScroll: true })
    }
  }

  function update() {
    panel.dataset.active = String(enabled)
    if (!enabled) {
      sheet.hidden = true
      sheet.inert = true
      content.inert = true
      restore.hidden = true
      restore.inert = true
      return
    }
    restore.inert = false
    const mobile = compact.matches
    const collapsed = mobile && state === 'collapsed'
    const active = panel.ownerDocument.activeElement
    // Make destinations available before moving focus out of a soon-hidden subtree.
    if (collapsed) restore.hidden = false
    if (!collapsed) {
      sheet.hidden = false
      sheet.inert = false
      content.inert = false
    }
    if (mobile) controls.hidden = false
    sheet.hidden = collapsed
    panel.dataset.sheet = state
    panel.style.setProperty('--sheet-height', `${geometry[state]}px`)
    const mapUnavailable = mobile && (state === 'full' || !options.mapIsUsable())
    if (!mobile && active && (controls.contains(active) || restore.contains(active))) focus(content)
    else if (collapsed && active && sheet.contains(active)) focus(restore)
    else if (active && mapUnavailable && map.contains(active)) focus(collapsed ? restore : handle)
    else if (active && mobile && !collapsed && restore.contains(active)) focus(handle)

    sheet.hidden = collapsed
    sheet.inert = collapsed
    content.inert = collapsed
    controls.hidden = !mobile
    restore.hidden = !collapsed
    handle.setAttribute('aria-label', state === 'full' ? 'Lower places' : 'Expand places')
    handle.setAttribute('aria-expanded', String(state === 'full'))
    restore.setAttribute('aria-expanded', String(state !== 'collapsed'))
    const signature = `${state}:${mobile}:${geometry.partial}:${geometry.full}:${mapUnavailable}`
    if (signature !== lastUpdate) {
      lastUpdate = signature
      onChange()
    }
  }

  function cancelDrag() {
    const previous = drag
    drag = undefined
    delete panel.dataset.dragging
    if (previous?.moved) suppressClick = true
    if (previous && handle.hasPointerCapture(previous.id)) handle.releasePointerCapture(previous.id)
    panel.style.setProperty('--sheet-height', `${geometry[state]}px`)
  }

  function refresh() {
    // A collapsed/desktop header is hidden: briefly expose it for measurement,
    // then restore it synchronously without changing focus or committed state.
    const sheetHidden = sheet.hidden
    const controlsHidden = controls.hidden
    sheet.hidden = false
    controls.hidden = false
    geometry = sheetGeometry(options.availableHeight(), controls.getBoundingClientRect().height)
    sheet.hidden = sheetHidden
    controls.hidden = controlsHidden
    update()
  }

  function setState(next: SheetState) {
    cancelDrag()
    state = next
    refresh()
  }

  handle.addEventListener('pointerdown', (event) => {
    if (!enabled || !compact.matches || !event.isPrimary || event.button !== 0 || drag) return
    suppressClick = false
    drag = { id: event.pointerId, startY: event.clientY, startHeight: geometry[state], height: geometry[state], moved: false }
    handle.setPointerCapture(event.pointerId)
  })
  handle.addEventListener('pointermove', (event) => {
    if (!drag || event.pointerId !== drag.id) return
    const delta = drag.startY - event.clientY
    drag.moved ||= Math.abs(delta) >= 4
    if (drag.moved) panel.dataset.dragging = 'true'
    drag.height = Math.max(0, Math.min(geometry.full, drag.startHeight + delta))
    panel.style.setProperty('--sheet-height', `${drag.height}px`)
  })
  handle.addEventListener('pointerup', (event) => {
    if (!drag || event.pointerId !== drag.id) return
    const next = drag.moved ? nearestSheetState(drag.height, geometry) : state
    setState(next)
  })
  for (const type of ['pointercancel', 'lostpointercapture']) {
    handle.addEventListener(type, (event) => {
      if (drag && (event as PointerEvent).pointerId === drag.id) cancelDrag()
    })
  }
  handle.addEventListener('click', (event) => {
    if (!enabled) return
    if (suppressClick && event.detail !== 0) {
      suppressClick = false
      event.preventDefault()
      return
    }
    setState(state === 'full' ? 'partial' : 'full')
  })
  handle.addEventListener('keydown', (event) => {
    if (!enabled) return
    let next: SheetState
    switch (event.key) {
      case 'ArrowUp': next = stepSheetState(state, 'up'); break
      case 'ArrowDown': next = stepSheetState(state, 'down'); break
      case 'Home': next = 'collapsed'; break
      case 'End': next = 'full'; break
      default: return
    }
    event.preventDefault()
    setState(next)
  })
  restore.addEventListener('click', () => { if (enabled) setState('partial') })
  compact.addEventListener('change', () => {
    cancelDrag()
    refresh()
  })
  const observer = new ResizeObserver(() => {
    cancelDrag()
    refresh()
  })
  observer.observe(panel)
  observer.observe(controls)
  refresh()
  function setActive(active: boolean) {
    cancelDrag()
    enabled = active
    refresh()
  }
  return { setState, refresh, cancelDrag, getState: () => state, setActive }
}
